<template>
  <div class="help-page">
    <section class="help-hero">
      <b-card
        no-body
        class="help-hero-card"
        :style="{ backgroundImage: `url(${require('@/assets/images/banner/banner.png')})` }"
      >
        <b-card-body class="help-hero-body">
          <h2 class="help-hero-title">
            Hello, how can we help?
          </h2>

          <b-form class="help-search-form">
            <b-input-group class="input-group-merge help-search-group">
              <b-input-group-prepend is-text>
                <feather-icon
                  icon="SearchIcon"
                  size="16"
                />
              </b-input-group-prepend>
              <b-form-input
                id="searchbar"
                v-model="search"
                placeholder="Search a question..."
              />
            </b-input-group>
          </b-form>

          <p class="help-hero-subtitle">
            Common troubleshooting topics: eCommerce, Blogging to payment
          </p>
        </b-card-body>
      </b-card>
    </section>

    <section class="help-popular">
      <h3 class="help-popular-title">
        Popular Articles
      </h3>

      <div
        v-if="isSideDataLoading"
        class="help-loading"
      >
        <b-spinner label="Loading..." />
      </div>

      <div
        v-else-if="!displayArticles.length"
        class="help-loading text-muted"
      >
        {{ search.trim() ? 'No help articles match your search.' : 'No help articles available yet.' }}
      </div>

      <b-row
        v-else
        class="help-cards-row"
      >
        <b-col
          v-for="(item, index) in displayArticles"
          :key="item.id || `${item.section}-${index}`"
          lg="4"
          md="6"
          cols="12"
          class="mb-2"
        >
          <b-card class="help-article-card text-center h-100">
            <div class="help-article-icon-wrap">
              <feather-icon
                :icon="getArticleIcon(index)"
                size="34"
                class="help-article-icon"
              />
            </div>

            <h4 class="help-article-title">
              {{ item.section || defaultSectionTitle(index) }}
            </h4>

            <p class="help-article-content">
              {{ truncateContent(item.content) }}
            </p>

            <b-button
              variant="outline-primary"
              class="help-read-btn"
              @click="openArticle(item)"
            >
              Read More
            </b-button>
          </b-card>
        </b-col>
      </b-row>
    </section>

    <section
      v-if="moreArticles.length"
      class="help-more"
    >
      <h3 class="help-popular-title">
        Read More Articles
      </h3>

      <b-row class="help-cards-row">
        <b-col
          v-for="(item, index) in moreArticles"
          :key="`more-${item.id || index}`"
          lg="4"
          md="6"
          cols="12"
          class="mb-2"
        >
          <b-card class="help-article-card text-center h-100">
            <div class="help-article-icon-wrap">
              <feather-icon
                :icon="getArticleIcon(index + 3)"
                size="34"
                class="help-article-icon"
              />
            </div>

            <h4 class="help-article-title">
              {{ item.section || defaultSectionTitle(index + 3) }}
            </h4>

            <p class="help-article-content">
              {{ truncateContent(item.content) }}
            </p>

            <b-button
              variant="outline-primary"
              class="help-read-btn"
              @click="openArticle(item)"
            >
              Read More
            </b-button>
          </b-card>
        </b-col>
      </b-row>
    </section>

    <b-modal
      v-model="showArticleModal"
      centered
      size="lg"
      :title="activeArticle.section || 'Help Article'"
    >
      <p class="mb-0 help-article-full">
        {{ activeArticle.content }}
      </p>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BCardBody,
  BForm,
  BInputGroup,
  BFormInput,
  BButton,
  BInputGroupPrepend,
  BRow,
  BCol,
  BSpinner,
  BModal,
} from 'bootstrap-vue'
import { vendorPanelApi } from '@/services/vendorPanelApi'

export default {
  components: {
    BForm,
    BCard,
    BRow,
    BCol,
    BSpinner,
    BCardBody,
    BInputGroup,
    BFormInput,
    BInputGroupPrepend,
    BButton,
    BModal,
  },
  data() {
    return {
      accessToken: localStorage.getItem('accessToken'),
      baseApi: process.env.VUE_APP_APIENDPOINT,
      isSideDataLoading: false,
      loadingTimeoutId: null,
      data: [],
      search: '',
      showArticleModal: false,
      activeArticle: {},
    }
  },
  computed: {
    filteredSections() {
      const term = this.search.trim().toLowerCase()
      if (!term) return this.data
      return this.data.filter(section => {
        const title = (section.section || '').toLowerCase()
        const content = (section.content || '').toLowerCase()
        return title.includes(term) || content.includes(term)
      })
    },
    displayArticles() {
      return this.filteredSections.slice(0, 3)
    },
    moreArticles() {
      return this.filteredSections.slice(3)
    },
  },
  mounted() {
    this.getHelp()
  },
  beforeDestroy() {
    if (this.loadingTimeoutId) {
      clearTimeout(this.loadingTimeoutId)
      this.loadingTimeoutId = null
    }
  },
  methods: {
    openArticle(item) {
      this.activeArticle = item || {}
      this.showArticleModal = true
    },
    async getHelp() {
      this.isSideDataLoading = true
      if (this.loadingTimeoutId) {
        clearTimeout(this.loadingTimeoutId)
      }
      this.loadingTimeoutId = setTimeout(() => {
        this.isSideDataLoading = false
      }, 15000)

      if (!this.accessToken || !this.baseApi) {
        this.data = []
        this.isSideDataLoading = false
        clearTimeout(this.loadingTimeoutId)
        this.loadingTimeoutId = null
        return
      }

      try {
        const response = await vendorPanelApi.help()
        const payload = response?.data?.data || {}
        const content = payload.content

        if (Array.isArray(content)) {
          this.data = content
            .map((item, index) => ({
              id: `help-${index}`,
              section: item?.question || 'Help',
              content: item?.answer || '',
            }))
            .filter(item => item.section || item.content)
        } else if (typeof content === 'string' && content.trim()) {
          this.data = [{ id: 'vendor-help', section: 'Help', content: content.trim() }]
        } else {
          this.data = []
        }
      } catch (error) {
        this.data = []
      } finally {
        this.isSideDataLoading = false
        clearTimeout(this.loadingTimeoutId)
        this.loadingTimeoutId = null
      }
    },
    getArticleIcon(index) {
      const icons = ['RocketIcon', 'GiftIcon', 'CreditCardIcon']
      return icons[index % icons.length]
    },
    truncateContent(content) {
      if (!content) return 'This article will guide you through the core details.'
      const text = String(content).trim()
      if (text.length <= 84) return text
      return `${text.slice(0, 84)}...`
    },
    defaultSectionTitle(index) {
      const names = ['Getting Started', 'First Steps', 'Add External Content']
      return names[index] || 'Help Article'
    },
  },
}
</script>

<style lang="scss" scoped>
.help-page {
  .help-hero-card {
    border: 0;
    border-radius: 8px;
    overflow: hidden;
    background-size: cover;
    background-position: center;
    min-height: 240px;
  }

  .help-hero-body {
    min-height: 240px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 2rem 1rem;
  }

  .help-hero-title {
    color: #4b465c;
    font-weight: 700;
    font-size: 2rem;
    margin-bottom: 1rem;
  }

  .help-search-form {
    width: 100%;
    max-width: 450px;
  }

  .help-search-group {
    ::v-deep .input-group-prepend .input-group-text {
      background: #fff;
      border-right: 0;
      color: #b3b0c4;
    }

    ::v-deep .form-control {
      border-left: 0;
      border-radius: 0 6px 6px 0;
      height: 38px;
      font-size: 0.92rem;
      color: #6f6b86;
    }
  }

  .help-hero-subtitle {
    margin-top: 0.75rem;
    margin-bottom: 0;
    font-weight: 400;
    font-size: 0.96rem;
    color: #6f6b86;
  }

  .help-popular {
    margin-top: 2.3rem;
  }

  .help-popular-title {
    text-align: center;
    color: #4b465c;
    font-weight: 700;
    font-size: 2rem;
    margin-bottom: 1.75rem;
  }

  .help-loading {
    height: 260px;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .help-cards-row {
    justify-content: center;
  }

  .help-article-card {
    border: 1px solid #dbdade;
    border-radius: 6px;
    box-shadow: none;

    .card-body {
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      padding: 1.4rem 1rem 1.25rem;
    }
  }

  .help-article-icon-wrap {
    height: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.8rem;
  }

  .help-article-icon {
    color: #5f5a77;
    stroke-width: 1.8;
  }

  .help-article-title {
    color: #4b465c;
    font-size: 1.35rem;
    font-weight: 700;
    margin-bottom: 0.45rem;
  }

  .help-article-content {
    color: #6f6b86;
    font-size: 0.95rem;
    margin-bottom: 0.9rem;
    min-height: 48px;
  }

  .help-read-btn {
    font-size: 0.8rem;
    font-weight: 600;
    color: #5f5a77;
    border-color: #d9d7e6;
    background: #f4f3ff;
    padding: 0.3rem 0.8rem;

    &:hover,
    &:focus,
    &:active {
      background: #ece9ff !important;
      border-color: #d2ceed !important;
      color: #4b465c !important;
    }
  }
}
</style>
